import instance from 'lib/api/axios';
import { getApiData } from 'lib/api/utils';
import { DEVICE_TOKEN_KEY, SESSION_KEY, SESSION_ONLY_KEY } from 'new/signin/constants';
import { clearStoredTokens } from 'new/signin/accessState';

/** What the API hands over when a sign-in succeeds (Account/login's TokenResult, and the new sign-in's signed-in answer). */
export type TSessionTokens = {
  token: string;
  refreshToken: string;
  roles: string[];
  deviceToken?: string | null;
};

let authHeaderAdded = false;

/** Sends the stored token with every request from now on. */
const addAuthHeader = () => {
  if (authHeaderAdded) {
    return;
  }
  authHeaderAdded = true;
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    return config;
  });
};

/**
 * Stores a new session (token, refresh token, remembered device, roles), sends
 * the token from now on, and loads the person (GET Account/me). Used by the
 * current sign-in page and the new one. `keepSignedIn` is only passed by the
 * new sign-in: false marks the session as this browser visit's only.
 */
export const startSession = async (tokens: TSessionTokens, options?: { keepSignedIn: boolean }) => {
  localStorage.setItem('token', tokens.token);
  localStorage.setItem('refreshToken', tokens.refreshToken);
  if (tokens.deviceToken) {
    localStorage.setItem(DEVICE_TOKEN_KEY, tokens.deviceToken);
  }
  if (options) {
    if (options.keepSignedIn) {
      localStorage.removeItem(SESSION_ONLY_KEY);
    } else {
      localStorage.setItem(SESSION_ONLY_KEY, '1');
      sessionStorage.setItem(SESSION_KEY, '1');
    }
  }
  addAuthHeader();
  localStorage.setItem('roles', JSON.stringify(tokens.roles));
  return getApiData('Account/me');
};

/** Forgets the stored session and stops sending its token. */
export const clearSession = (): void => {
  clearStoredTokens();
  const headers = instance.defaults?.headers as Record<string, unknown> & { common?: Record<string, unknown> } | undefined;
  if (headers) {
    delete headers.Authorization;
    if (headers.common) {
      delete headers.common.Authorization;
    }
  }
};

/**
 * "Keep me signed in" was off and the browser has been closed since (its
 * sessionStorage is empty): the session ends here. Called on app start, only
 * while the new sign-in is on.
 */
export const endClosedBrowserSession = (): boolean => {
  if (isClosedBrowserSession()) {
    clearSession();
    return true;
  }
  return false;
};

/**
 * Whether endClosedBrowserSession would end the session, without ending it.
 * App start uses it to ask for the signed-in person at the same time as the
 * sign-in settings whenever there is nothing to end.
 */
export const isClosedBrowserSession = (): boolean => {
  try {
    return Boolean(localStorage.getItem(SESSION_ONLY_KEY) && !sessionStorage.getItem(SESSION_KEY));
  } catch {
    // Storage blocked: nothing was kept to end.
    return false;
  }
};
